import { useEffect, useCallback } from 'react';
import type { Place } from '../types/warehouse.types';
import { getPlaceById } from '../utils/placeUtils';
import type { ViewState } from '../shared/types';

interface UseHighlightZoomParams {
  highlightedPlaceIds: string[]; // Specific place IDs from selection (format: "A-1-2-3")
  places: Map<string, Place>;
  viewState: ViewState;
  canvasSize: { width: number; height: number };
  setViewState: React.Dispatch<React.SetStateAction<ViewState>>;
}

/**
 * Hook to automatically zoom and center on highlighted places
 */
export const useHighlightZoom = ({
  highlightedPlaceIds,
  places,
  viewState,
  canvasSize,
  setViewState,
}: UseHighlightZoomParams) => {
  
  const zoomToHighlightedPlaces = useCallback(() => {
    if (highlightedPlaceIds.length === 0) {
      return;
    }
    
    if (places.size === 0) {
      return;
    }

    // Collect all highlighted places
    const targetPlaces: Place[] = [];
    
    // Add places by place_id or numeric id (storage_place_id)
    highlightedPlaceIds.forEach((placeId) => {
      const place = getPlaceById(places, placeId);
      if (place) {
        targetPlaces.push(place);
      }
    });

    if (targetPlaces.length === 0) {
      return;
    }

    // Calculate bounding box of all highlighted places in world coordinates
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    targetPlaces.forEach(place => {
      const x = place.baseX;
      const y = place.baseY;
      const width = place.placeDimensions.width;
      const height = place.placeDimensions.height;

      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x + width);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y + height);
    });

    // Add padding around the highlighted places
    const padding = 50; // Reduced padding for better fit
    minX -= padding;
    maxX += padding;
    minY -= padding;
    maxY += padding;

    const worldWidth = maxX - minX;
    const worldHeight = maxY - minY;

    // Calculate scale to fit the bounding box
    const scaleX = canvasSize.width / worldWidth;
    const scaleY = canvasSize.height / worldHeight;
    const newScale = Math.min(scaleX, scaleY, 3.0); // Increased max scale to 3.0 for better zoom

    // Calculate center of highlighted places in world coordinates
    const centerWorldX = (minX + maxX) / 2;
    const centerWorldY = (minY + maxY) / 2;

    // Calculate offsets to center the view on the highlighted places
    // The transformation is: screenX = (worldX + offsetX) * scale
    // We want centerWorldX to map to canvas center (canvasSize.width / 2)
    // So: canvasSize.width / 2 = (centerWorldX + offsetX) * newScale
    // Therefore: offsetX = (canvasSize.width / 2) / newScale - centerWorldX
    const newOffsetX = (canvasSize.width / 2) / newScale - centerWorldX;
    const newOffsetY = (canvasSize.height / 2) / newScale - centerWorldY;

    setViewState(prev => ({
      ...prev,
      offsetX: newOffsetX,
      offsetY: newOffsetY,
      scale: newScale,
    }));
  }, [highlightedPlaceIds, places, canvasSize, setViewState]);

  return { zoomToHighlightedPlaces };
};
